import { useMutation } from "@tanstack/vue-query";

import type { Newsletter } from "@/types/newsletter";

import { type BasicSubscriber, type SubscribeResult, subscribe } from "../lib";

declare var REFERRING_SUBSCRIBER_ID: string | undefined;

interface UseSubscribeOptions {
  newsletter: Newsletter;
  onSuccess?: (subscriber: BasicSubscriber) => void;
  onError?: (error: string | object, data?: any) => void;
}

export function useSubscribe({
  newsletter,
  onSuccess,
  onError,
}: UseSubscribeOptions) {
  return useMutation({
    async mutationFn(params: {
      emailAddress: string;
      metadata: Record<string, string>;
      turnstileResponseToken: string;
      formId: string | null;
    }): Promise<SubscribeResult> {
      // The archive page mounts this form (via the subscribe modal) without the
      // subscribe-page snippet that declares REFERRING_SUBSCRIBER_ID, so a bare
      // reference throws a ReferenceError that vue-query swallows — the request
      // never fires. Guard it the way App.vue guards EMAIL_ID.
      const referringSubscriberId =
        typeof REFERRING_SUBSCRIBER_ID !== "undefined"
          ? REFERRING_SUBSCRIBER_ID
          : null;
      return subscribe(
        params.emailAddress,
        params.metadata,
        newsletter,
        referringSubscriberId || null,
        params.turnstileResponseToken || null,
        params.formId
      );
    },
    onSuccess(result) {
      if ("subscriber" in result) {
        onSuccess?.(result.subscriber);
      } else {
        onError?.(result.error, result.data);
      }
    },
  });
}
